"use client";
// Owns the search page's filter state and renders the filtered, location-grouped
// results. Client-side by design: the server already drew ONE random unit per
// model, so refetching per checkbox would reshuffle the list mid-browse.
import { Fragment, useMemo, useState } from "react";
import VehicleCard from "./VehicleCard";
import SearchFilters, { FACETS, buildFacets, carMatches, perDayOf } from "./SearchFilters";
import styles from "./search-results.module.css";

const emptySelection = () => Object.fromEntries(FACETS.map((f) => [f.key, new Set()]));

export default function SearchResults({ cars = [], points, dates, currency, pickupName, urlData, filters, activeFilters = [], enabledFacets, catalogue = false, agency = false }) {
    const facets = useMemo(() => buildFacets(cars), [cars]);
    const priceBounds = useMemo(() => {
        const vals = cars.map(perDayOf).filter((n) => Number.isFinite(n) && n > 0);
        return vals.length
            ? { min: Math.min(...vals), max: Math.max(...vals) }
            : { min: 0, max: 0 };
    }, [cars]);

    const [selected, setSelected] = useState(emptySelection);
    const [maxPrice, setMaxPrice] = useState(() => Math.ceil(priceBounds.max));
    const [panelOpen, setPanelOpen] = useState(false);

    const toggle = (key, value) =>
        setSelected((prev) => {
            const next = new Set(prev[key]);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return { ...prev, [key]: next };
        });

    const clear = () => {
        setSelected(emptySelection());
        setMaxPrice(Math.ceil(priceBounds.max));
    };

    const activeTotal =
        Object.values(selected).reduce((s, set) => s + set.size, 0) +
        (maxPrice < Math.ceil(priceBounds.max) ? 1 : 0);
    const filtering = activeTotal > 0;

    // Filters rank rather than remove: matches lead, everything else still
    // follows under "Other vehicles" so the visitor never hits a dead end.
    const [matched, others] = useMemo(() => {
        if (!filtering) return [cars, []];
        const hit = [];
        const miss = [];
        for (const c of cars) (carMatches(c, selected, maxPrice) ? hit : miss).push(c);
        return [hit, miss];
    }, [cars, selected, maxPrice, filtering]);

    const symbol = cars[0]?.breakdown?.currency ?? cars[0]?.currency ?? "€";

    // `list` arrives already ordered (searched city first, then cheapest group,
    // price asc inside) — restart the heading whenever the location changes.
    const renderCars = (list) =>
        list.map((car, i) => {
            const loc = String(car?.carLocation ?? "");
            const newGroup = i === 0 || loc !== String(list[i - 1]?.carLocation ?? "");
            return (
                <Fragment key={car?.vid}>
                    {newGroup && loc && (
                        <h3 className={styles.groupHead}>
                            <span className={styles.groupName}>{loc}</span>
                            {Boolean(pickupName) && !car?.atPickupCity && (
                                <span className={styles.groupNote}>
                                    not available in {pickupName} · delivered from {loc}
                                </span>
                            )}
                        </h3>
                    )}
                    <VehicleCard car={car} points={points} dates={dates} currency={currency} catalogue={catalogue} agency={agency} />
                </Fragment>
            );
        });

    return (
        <div className={styles.layout}>
            <button
                type="button"
                className={styles.filterToggle}
                onClick={() => setPanelOpen((o) => !o)}
                aria-expanded={panelOpen}
            >
                Search &amp; filters{activeTotal > 0 ? ` · ${activeTotal}` : ""}
            </button>

            <div className={`${styles.railWrap} ${panelOpen ? styles.railOpen : ""}`}>
                <SearchFilters
                    facets={facets}
                    selected={selected}
                    onToggle={toggle}
                    onClear={clear}
                    priceBounds={priceBounds}
                    maxPrice={maxPrice}
                    onMaxPrice={setMaxPrice}
                    currency={symbol}
                    urlData={urlData}
                    filters={filters}
                    activeFilters={activeFilters}
                    enabledFacets={enabledFacets}
                />
            </div>

            <div className={styles.results}>
                {matched.length === 0 ? (
                    <div className={styles.noMatch}>
                        <span className={styles.noMatchIcon} aria-hidden>🔍</span>
                        <h2 className={styles.noMatchTitle}>No cars match these filters</h2>
                        <p className={styles.noMatchText}>
                            The cars below don&apos;t match, but are still available for your dates.
                        </p>
                        <button type="button" className={styles.noMatchBtn} onClick={clear}>
                            Clear all filters
                        </button>
                    </div>
                ) : (
                    renderCars(matched)
                )}

                {others.length > 0 && (
                    <>
                        <div className={styles.otherHead}>
                            <h2 className={styles.otherTitle}>More vehicles in the list</h2>
                            <span className={styles.otherNote}>
                                {others.length} outside your filters
                            </span>
                        </div>
                        {renderCars(others)}
                    </>
                )}
            </div>
        </div>
    );
}
